import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHousingData, DISTRICT_VI_MAP } from '../hooks/useHousingData';
import FilterBar from '../components/FilterBar';
import HousingCard from '../components/HousingCard';
import SkeletonCard from '../components/SkeletonCard';
import NearMePanel from '../components/NearMePanel';
import { PROPERTY_TYPES } from '../constants';
import { geocodeLocation, syncGeocodeCacheToD1, getGeocodeCache } from '../services/mapService';
import { getDistance } from '../utils/distance';

// Strip Vietnamese diacritics for accent-insensitive search
const removeAccents = (str) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

// Infinite scroll page size: mobile 20, desktop 50
const getPageSize = () => (window.innerWidth < 768 ? 20 : 50);

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const { properties, loading, error, dataSource, getLocations, refresh } = useHousingData();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive filters from URL search params (stable reference to avoid unnecessary re-renders)
  const searchQuery = useMemo(() => searchParams.get('q') || '', [searchParams]);
  const filters = useMemo(() => ({
    location: searchParams.get('location') || '',
    type: searchParams.get('type') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    amenities: searchParams.get('amenities') ? searchParams.get('amenities').split(',') : [],
  }), [searchParams]);

  // Debounced search input: local state for smooth typing,
  // URL filter applied 1s after the last keystroke
  const [searchInput, setSearchInput] = useState(searchQuery);
  const searchDebounceRef = useRef(null);
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);
  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);
  const handleSearchInput = (value) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('q', value);
        else next.delete('q');
        return next;
      });
    }, 1000);
  };

  // Clear the search text and filter immediately
  const clearSearch = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchInput('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('q');
      return next;
    });
  };

  const setFilters = (updater) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const newFilters = typeof updater === 'function' ? updater(filters) : updater;
      if (newFilters.location) next.set('location', newFilters.location);
      else next.delete('location');
      if (newFilters.type) next.set('type', newFilters.type);
      else next.delete('type');
      if (newFilters.minPrice) next.set('minPrice', newFilters.minPrice);
      else next.delete('minPrice');
      if (newFilters.maxPrice) next.set('maxPrice', newFilters.maxPrice);
      else next.delete('maxPrice');
      if (newFilters.amenities.length > 0) next.set('amenities', newFilters.amenities.join(','));
      else next.delete('amenities');
      return next;
    });
  };

  // Near Me state
  const [userLocation, setUserLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [showNearby, setShowNearby] = useState(false);
  const [geocodingNearby, setGeocodingNearby] = useState(false);
  const [nearbyProperties, setNearbyProperties] = useState([]);
  const NEARBY_RADIUS_KM = 2;
  const geocodingRef = useRef(false);

  const locations = getLocations();

  // Build a fallback geocoding query from displayAddress -> district+city.
  // NOTE: additionalAddresses are handled FIRST by the geocoding loop below.
  // This function is only for fallback after additional addresses fail.
  const buildAddressQuery = useCallback((property) => {
    const address = property.displayAddressVi || property.displayAddress || property.addressVi || property.address || '';
    const district = property.districtVi || property.district || '';
    const city = property.cityVi || property.city || 'Ho Chi Minh City';
    if (address && district) return `${address}, ${district}, ${city}`;
    if (address) return `${address}, ${city}`;
    if (district) return `${district}, ${city}`;
    return null;
  }, []);

  // When user location is obtained, geocode properties without lat/lng
  useEffect(() => {
    if (!userLocation) {
      setNearbyProperties([]);
      return;
    }

    // Start with properties that already have coordinates
    const alreadyReady = properties
      .filter((p) => p.available !== false && p.lat != null && p.lng != null)
      .map((p) => ({ ...p, _geocodeFailed: false }));

    // Properties needing geocoding
    const needGeocoding = properties.filter((p) => {
      if (p.available === false) return false;
      if (p.lat != null && p.lng != null) return false;
      return !!buildAddressQuery(p);
    });

    setNearbyProperties(alreadyReady);

    if (needGeocoding.length === 0) return;

    // Prevent duplicate runs
    if (geocodingRef.current) return;
    geocodingRef.current = true;
    setGeocodingNearby(true);

    let cancelled = false;

    const runGeocoding = async () => {
      // Use the shared geocodeCache from mapService so results are visible
      // to syncGeocodeCacheToD1() and other consumers.
      const cache = getGeocodeCache();

      for (const prop of needGeocoding) {
        if (cancelled) return;

        const addressQuery = buildAddressQuery(prop);
        if (!addressQuery) continue;

        const cacheKey = addressQuery.toLowerCase().trim();
        let coords = cache[cacheKey];

        if (!coords) {
          try {
            // If property has additionalAddresses, try each one
            if (prop.additionalAddresses && prop.additionalAddresses.length > 0) {
              for (const addr of prop.additionalAddresses) {
                if (!addr || !addr.trim()) continue;
                const addrCacheKey = addr.toLowerCase().trim();
                coords = cache[addrCacheKey];
                if (!coords) {
                  coords = await geocodeLocation(addr, prop.cityVi || prop.city || '');
                  if (coords) {
                    cache[addrCacheKey] = coords;
                    break;
                  }
                  // Rate limit pause
                  await new Promise(r => setTimeout(r, 500));
                } else {
                  break;
                }
              }
            }
            // Fallback to the address query if no coordinates found yet
            if (!coords) {
              coords = await geocodeLocation(addressQuery, prop.cityVi || prop.city || '');
              if (coords) {
                cache[cacheKey] = coords;
              }
            }
          } catch {
            coords = null;
          }
        }

        if (!cancelled) {
          // Only add geocoded properties that are within 2km radius
          if (coords) {
            const dist = getDistance(userLocation.lat, userLocation.lng, coords.lat, coords.lng);
            if (dist <= NEARBY_RADIUS_KM) {
              setNearbyProperties((prev) => {
                if (prev.some((p) => p.id === prop.id)) return prev;
                return [
                  ...prev,
                  {
                    ...prop,
                    lat: coords.lat,
                    lng: coords.lng,
                    distance: dist,
                    _geocodeFailed: false,
                  },
                ];
              });
            }
          } else {
            // Still mark geocoding failed so we can track progress
            setNearbyProperties((prev) => {
              if (prev.some((p) => p.id === prop.id)) return prev;
              return [
                ...prev,
                {
                  ...prop,
                  lat: null,
                  lng: null,
                  _geocodeFailed: true,
                },
              ];
            });
          }
        }
      }

      geocodingRef.current = false;
      setGeocodingNearby(false);

      // Auto-push newly geocoded results to D1 so they persist
      // for other users/sessions. Fire-and-forget — don't block the UI.
      syncGeocodeCacheToD1()
        .then(r => {
          if (r.success && r.count > 0) {
            console.log(`[Geocode] Auto-pushed ${r.count} entries to D1 after Near Me geocoding`);
          }
        })
        .catch(err => console.warn('[Geocode] Auto-push to D1 failed:', err));
    };

    runGeocoding();

    return () => {
      cancelled = true;
    };
  }, [userLocation, properties, buildAddressQuery]);

  const handleFindNearMe = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError(t('nearMe.unavailable'));
      return;
    }

    setLocating(true);
    setLocationError(null);
    setShowNearby(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocating(false);
        setTimeout(() => {
          document.getElementById('near-me-section')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      },
      (error) => {
        setLocating(false);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationError(t('nearMe.denied'));
        } else {
          setLocationError(t('nearMe.unavailable'));
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  }, [t]);

  const handleCloseNearby = useCallback(() => {
    setShowNearby(false);
    setUserLocation(null);
    setLocationError(null);
  }, []);

  const filteredHousing = useMemo(() => {
    const isVietnamese = i18n.language === 'vi';
    return properties.filter((item) => {
      if (searchQuery) {
        const q = removeAccents(searchQuery.toLowerCase());
        const matchesSearch =
          removeAccents(item.title.toLowerCase()).includes(q) ||
          removeAccents(item.titleVi?.toLowerCase() || '').includes(q) ||
          removeAccents(item.district.toLowerCase()).includes(q) ||
          removeAccents(item.districtVi?.toLowerCase() || '').includes(q) ||
          removeAccents(item.city.toLowerCase()).includes(q) ||
          removeAccents(item.cityVi?.toLowerCase() || '').includes(q);
        if (!matchesSearch) return false;
      }

      if (filters.location) {
        const itemDistrict = isVietnamese
          ? (item.districtVi || DISTRICT_VI_MAP[item.district] || item.district)
          : item.district;
        if (itemDistrict !== filters.location) return false;
      }
      if (filters.minPrice && item.price < parseInt(filters.minPrice)) return false;
      if (filters.maxPrice && item.price > parseInt(filters.maxPrice)) return false;
      if (filters.type) {
        // Normalize both sides to NFC to handle Unicode composition mismatches
        // (e.g. NFD vs NFC for Vietnamese characters in property.txt files)
        const filterType = filters.type.normalize('NFC');
        const itemType = (item.type || '').normalize('NFC');
        if (itemType !== filterType) return false;
      }
      if (filters.amenities.length > 0) {
        const hasAllAmenities = filters.amenities.every((amenity) =>
          item.amenities.includes(amenity)
        );
        if (!hasAllAmenities) return false;
      }

      return true;
    });
  }, [searchQuery, filters, properties, i18n.language, i18n]);

  // ==================== Infinity Scroll ====================
  // Page size: 20 on mobile, 50 on desktop. Recompute on resize.
  const [pageSize, setPageSize] = useState(() => getPageSize());
  const [visibleCount, setVisibleCount] = useState(() => pageSize);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const loadTimerRef = useRef(null);

  // Listen for window resize to switch between mobile/desktop page size
  useEffect(() => {
    const handleResize = () => {
      const newSize = getPageSize();
      setPageSize((prev) => {
        if (prev !== newSize) {
          setVisibleCount((prevVisible) => Math.max(newSize, prevVisible));
          return newSize;
        }
        return prev;
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset visible count whenever filter results change
  useEffect(() => {
    setVisibleCount(pageSize);
    setLoadingMore(false);
    loadingMoreRef.current = false;
  }, [filteredHousing.length, searchQuery, filters, pageSize]);

  // IntersectionObserver — when the sentinel enters the viewport, load the next batch
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        // Guard against concurrent loads
        if (loadingMoreRef.current) return;

        const hasMore = visibleCount < filteredHousing.length;
        if (!hasMore) return;

        setLoadingMore(true);
        loadingMoreRef.current = true;

        // Simulate slight delay for smooth visual feedback (cards animate in)
        if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
        loadTimerRef.current = setTimeout(() => {
          const size = pageSize || getPageSize();
          setVisibleCount((prev) => Math.min(prev + size, filteredHousing.length));
          setLoadingMore(false);
          loadingMoreRef.current = false;
        }, 600);
      },
      { rootMargin: '200px 0px' } // trigger slightly before hitting the bottom
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
      if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    };
  }, [visibleCount, filteredHousing.length, pageSize]);

  const visibleHousing = useMemo(() => {
    // When filters change, filteredHousing.length shrinks — ensure we don't exceed it.
    return filteredHousing.slice(0, Math.min(visibleCount, filteredHousing.length));
  }, [filteredHousing, visibleCount]);

  const hasMore = visibleCount < filteredHousing.length;
  // ==================== /Infinity Scroll ====================

  const resetFilters = () => {
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || filters.location || filters.type || filters.minPrice || filters.maxPrice || filters.amenities.length > 0;

  // Pick the right results label key based on selected type
  const getResultsLabelKey = () => {
    if (!filters.type) return 'listing.results';
    // Normalize the filter type to NFC to handle Unicode composition mismatches
    const normalizedType = filters.type.normalize('NFC');
    const typeToKey = {
      'Ký túc xá': 'listing.resultsKyTucXa',
      'Sleepbox': 'listing.resultsSleepbox',
      'Miniduplex': 'listing.resultsMiniduplex',
      'Phòng (1 - 2 người)': 'listing.resultsPhong',
    };
    return typeToKey[normalizedType] || 'listing.results';
  };
  const resultsLabelKey = getResultsLabelKey();

  // District chip options (localized, derived from data)
  const isVietnamese = i18n.language === 'vi';
  const districtOptions = useMemo(() => {
    const set = new Set();
    for (const p of properties) {
      set.add(isVietnamese
        ? (p.districtVi || DISTRICT_VI_MAP[p.district] || p.district)
        : p.district);
    }
    return [...set].filter(Boolean).sort((a, b) => a.localeCompare(b, isVietnamese ? 'vi' : 'en'));
  }, [properties, isVietnamese]);

  // Card view density (cards per row): 1, 2, or 4 — persisted in URL.
  // Default is 2 (collage image stack); single image only in dense 4-col view.
  const rawCols = parseInt(searchParams.get('viewCols') || '2', 10);
  const activeView = [1, 2, 4].includes(rawCols) ? rawCols : 2;
  const setViewCols = (n) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('viewCols', String(n));
      return next;
    });
  };
  const gridColsClass = {
    1: 'grid-cols-1 max-w-2xl mx-auto',
    2: 'grid-cols-1 sm:grid-cols-2',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  }[activeView];

  // During Drive load, show properties as they stream in instead of a full-page spinner
  const showLoadingScreen = loading && dataSource !== 'google_drive' && properties.length === 0;

  if (showLoadingScreen) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ============ Hero (mobile-first) ============ */}
      <section className="relative bg-gradient-to-b from-primary-600 via-primary-600 to-primary-800 text-white overflow-hidden rounded-b-[2.5rem]">
        {/* Decorative shapes */}
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-white/10 blur-2xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-primary-400/20 blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 sm:pt-12 sm:pb-16 md:pb-20">
          <div className="text-center max-w-2xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full px-3.5 py-1.5 text-xs sm:text-sm text-primary-100 mb-4">
              <svg className="w-4 h-4 text-yellow-300 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1l3 6.3 6.9 1-5 4.9 1.2 6.9L12 16.9 5.9 20.1 7.1 13.2l-5-4.9 6.9-1z" />
              </svg>
              <span>{t('home.heroBadge')}</span>
            </div>

            <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold mb-3 leading-tight">
              {t('hero.title')}{' '}
              <span className="text-yellow-300">{t('hero.titleHighlight')}</span>
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-primary-100 mb-6 max-w-xl mx-auto">
              {t('hero.subtitle')}
            </p>

            {/* Search row — 8/2 split (search input / find nearby) */}
            <div className="grid grid-cols-1 sm:grid-cols-10 gap-2.5 max-w-xl mx-auto">
              <div className="sm:col-span-8 flex items-center bg-white rounded-xl shadow-lg px-4 focus-within:ring-2 focus-within:ring-yellow-300/60 transition-shadow">
                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  placeholder={t('hero.searchPlaceholder')}
                  className="w-full px-3 py-3 text-gray-900 placeholder-gray-400 bg-transparent border-none focus:outline-none text-sm sm:text-base"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    aria-label={t('common.cancel')}
                    title={t('common.cancel')}
                    className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <button
                onClick={handleFindNearMe}
                disabled={locating}
                className="sm:col-span-2 inline-flex items-center justify-center gap-1 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-semibold px-2 py-3 rounded-xl shadow-lg transition-all duration-200 text-xs sm:text-sm whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98]"
              >
                {locating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                    <span>{t('nearMe.locating')}</span>
                  </>
                ) : (
                  <>
                    <span>📍</span>
                    <span className="truncate">{t('nearMe.button')}</span>
                  </>
                )}
              </button>
            </div>

            {/* Location Error */}
            {locationError && (
              <div className="mt-3 bg-red-500/20 backdrop-blur-sm border border-red-300/30 text-red-100 text-sm rounded-xl px-4 py-2.5 max-w-md mx-auto text-left">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span>{locationError}</span>
                  <button onClick={() => setLocationError(null)} className="ml-auto text-red-200 hover:text-red-100">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </section>

      {/* ============ Quick type chips (mobile-first horizontal scroll) ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-5 relative z-10">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('home.quickTypes')}</p>
          <Link
            to="/facets"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {t('home.exploreFacets')}
          </Link>
        </div>
        {/* Horizontal scroll on mobile, wraps on larger screens */}
        <div className="flex lg:flex-wrap gap-2 overflow-x-auto scrollbar-hide pb-1 lg:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0">
          <button
            type="button"
            onClick={() => setFilters((prev) => ({ ...prev, type: '' }))}
            className={`flex-shrink-0 lg:flex-shrink inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
              !filters.type
                ? 'border-primary-500 bg-primary-500 text-white shadow-sm'
                : 'border-gray-200 bg-white text-gray-600 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            🏠 {t('filter.allTypes')}
          </button>
          {PROPERTY_TYPES.map((type) => {
            const normalized = type.value.normalize('NFC');
            const active = filters.type && filters.type.normalize('NFC') === normalized;
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => setFilters((prev) => ({ ...prev, type: active ? '' : type.value }))}
                className={`flex-shrink-0 lg:flex-shrink inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                  active
                    ? 'border-primary-500 bg-primary-500 text-white shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                <span>{t(type.labelKey)}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ============ District chips (same pattern as type chips) ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{t('filter.location')}</p>
        <div className="flex lg:flex-wrap gap-2 overflow-x-auto scrollbar-hide pb-1 lg:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0">
          <button
            type="button"
            onClick={() => setFilters((prev) => ({ ...prev, location: '' }))}
            className={`flex-shrink-0 lg:flex-shrink inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
              !filters.location
                ? 'border-primary-500 bg-primary-500 text-white shadow-sm'
                : 'border-gray-200 bg-white text-gray-600 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            {t('filter.allLocations')}
          </button>
          {(districtOptions || locations).map((loc) => {
            const active = filters.location === loc;
            return (
              <button
                key={loc}
                type="button"
                onClick={() => setFilters((prev) => ({ ...prev, location: active ? '' : loc }))}
                className={`flex-shrink-0 lg:flex-shrink inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                  active
                    ? 'border-primary-500 bg-primary-500 text-white shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                📍 <span className="truncate">{loc}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ============ FilterBar (advanced: price + amenities) ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 mb-6">
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          onReset={resetFilters}
        />
      </section>

      {/* ============ Near Me Section ============ */}
      {showNearby && (
        <div id="near-me-section">
          <NearMePanel
            userLocation={userLocation}
            properties={nearbyProperties}
            onClose={handleCloseNearby}
            geocoding={geocodingNearby}
          />
        </div>
      )}

      {/* ============ Results Section ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Section header */}
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
              <span>🌟</span>
              <span>{hasActiveFilters ? t(resultsLabelKey, { count: filteredHousing.length }) : t('listing.title')}</span>
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {t(resultsLabelKey, { count: filteredHousing.length })}
              {filteredHousing.length === 0 && hasActiveFilters && (
                <span> · {t('listing.noResultsHint')}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {hasActiveFilters && (
              <button onClick={resetFilters} className="btn-outline text-sm px-4 py-2 whitespace-nowrap">
                {t('filter.reset')}
              </button>
            )}
            {/* View density switcher: 1 / 2 / 4 cards per row (hidden on mobile — not enough space) */}
            <div className="hidden sm:flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1" title={t('home.viewDensity')}>
              {[1, 2, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setViewCols(n)}
                  aria-label={`${n} ${t('home.cardsPerRow')}`}
                  className={`w-8 h-8 rounded-md flex flex-col items-center justify-center gap-0.5 transition-all ${
                    activeView === n
                      ? 'bg-primary-500 text-white'
                      : 'text-gray-400 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                >
                  <span className={`flex ${n === 1 ? 'flex-col' : 'flex-row'} gap-0.5`}>
                    {Array.from({ length: n === 4 ? 2 : n === 2 ? 2 : 1 }).map((_, i) => (
                      <span key={i} className={`block rounded-[1px] bg-current ${n === 1 ? 'w-2.5 h-1' : 'w-1 h-1'}`} />
                    ))}
                  </span>
                  <span className="sr-only">{n}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Property cards — show skeleton cards while Drive loads */}
        {filteredHousing.length > 0 || loading ? (
          <>
            <div className={`grid ${gridColsClass} gap-4 sm:gap-5`}>
              {visibleHousing.map((housing, idx) => (
                <div key={housing.id} className="animate-card-enter" style={{ animationDelay: `${Math.min(idx % 12, 11) * 40}ms` }}>
                  <HousingCard housing={housing} collage={activeView !== 4} />
                </div>
              ))}
              {/* Show skeleton cards while Drive is still loading properties */}
              {loading && dataSource === 'google_drive' && Array.from({ length: Math.max(8 - visibleHousing.length, 3) }).map((_, i) => (
                <SkeletonCard key={`skeleton-${i}`} />
              ))}
            </div>

            {/* Sentinel — IntersectionObserver triggers loading more when this becomes visible */}
            {hasMore && <div ref={sentinelRef} className="h-1" aria-hidden="true" />}

            {/* Loading more indicator (smooth animated) */}
            {loadingMore && hasMore && (
              <div className="flex items-center justify-center py-8 animate-count-up">
                <div className="flex flex-col items-center gap-3">
                  {/* Spinner */}
                  <div className="relative w-10 h-10">
                    <div className="absolute inset-0 rounded-full border-[3px] border-primary-100" />
                    <div className="absolute inset-0 rounded-full border-[3px] border-primary-500 border-t-transparent animate-spinner-rotate" />
                  </div>
                  <p className="text-sm text-gray-500">{t('listing.loadingMore')}</p>
                  {/* Mini skeleton preview while loading */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 w-full max-w-4xl mt-2">
                    <SkeletonCard />
                    <SkeletonCard />
                  </div>
                </div>
              </div>
            )}

            {/* End of list message */}
            {!hasMore && filteredHousing.length > 0 && (
              <div className="flex items-center justify-center py-8 animate-count-up">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <span className="w-10 h-px bg-gray-300" />
                  <span>{t('listing.endOfList')}</span>
                  <span className="w-10 h-px bg-gray-300" />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('listing.noResults')}</h3>
            <p className="text-sm text-gray-500">{t('listing.noResultsHint')}</p>
          </div>
        )}
      </section>

      {/* Error Banner */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-5 py-3 flex items-center justify-between">
            <p className="text-sm">{t('common.error')}: {error}</p>
            <button onClick={refresh} className="text-sm font-medium text-red-700 hover:text-red-900 underline ml-4">
              {t('common.retry')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;